import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export interface PageItem {
  sectionId: string;
  height: number;
  offsetY: number;
  // For split sections:
  clipStartY?: number;     // Start clipping from this Y within the section (0 = top)
  clipEndY?: number;       // Stop clipping at this Y within the section
  isContinuation?: boolean; // True if this is a continuation from previous page
  hasMore?: boolean;        // True if section continues on next page
  totalSectionHeight?: number; // Full height of section (for split sections)
}

export interface Page {
  pageNumber: number;
  items: PageItem[];
  contentHeight: number;
}

interface CalculatedLayout {
  pages: Page[];
  totalPages: number;
  sectionToPage: Map<string, number>;
}

interface UsePdfPageLayoutProps {
  contentContainerRef: React.RefObject<HTMLElement>;
  sectionOrder: string[];
  selectedSections: Record<string, boolean>;
  manualPageBreaks: Set<string>;
  keepTogether: Set<string>;
  pageContentHeight: number; // Height available for content on each page
  headerHeight?: number; // Height reserved for logo/header on first page
}

interface UsePdfPageLayoutReturn {
  pages: Page[];
  totalPages: number;
  sectionToPage: Map<string, number>;
  measureSections: () => void;
  isCalculating: boolean;
  sectionHeights: Map<string, number>;
}

export function usePdfPageLayout({
  contentContainerRef,
  sectionOrder,
  selectedSections,
  manualPageBreaks,
  keepTogether,
  pageContentHeight,
  headerHeight = 80
}: UsePdfPageLayoutProps): UsePdfPageLayoutReturn {
  const [sectionHeights, setSectionHeights] = useState<Map<string, number>>(new Map());
  const [isCalculating, setIsCalculating] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const measurementTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Measure all section heights with computed styles for accuracy
  const measureSections = useCallback(() => {
    if (!contentContainerRef.current) return;

    setIsCalculating(true);

    // Clear any pending measurement
    if (measurementTimeoutRef.current) {
      clearTimeout(measurementTimeoutRef.current);
    }

    // Increased delay to 300ms for DOM to fully settle
    measurementTimeoutRef.current = setTimeout(() => {
      const container = contentContainerRef.current;
      if (!container) {
        setIsCalculating(false);
        return;
      }

      const newHeights = new Map<string, number>();

      // Find all sections by data-section-id attribute
      const sectionElements = container.querySelectorAll('[data-section-id]');

      sectionElements.forEach((element) => {
        const sectionId = element.getAttribute('data-section-id');
        if (sectionId) {
          // IMPORTANT: Skip parent sections that contain child sections
          // This prevents double-counting space (parent contains children, measuring both would duplicate)
          const childSections = element.querySelectorAll('[data-section-id]');
          if (childSections.length > 0) {
            // This is a parent section - skip it, we'll measure the children instead
            console.log(`📏 Skipping parent section: ${sectionId} (contains ${childSections.length} child sections)`);
            return;
          }

          const rect = element.getBoundingClientRect();

          // Get computed styles for accurate margins
          const computedStyle = window.getComputedStyle(element as Element);
          const marginTop = parseFloat(computedStyle.marginTop) || 0;
          const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

          // Total height including margins plus a small safety buffer
          const totalHeight = Math.ceil(rect.height + marginTop + marginBottom) + 8;
          newHeights.set(sectionId, totalHeight);
        }
      });

      setSectionHeights(newHeights);
      setIsCalculating(false);
    }, 300); // Increased from 100ms to 300ms
  }, [contentContainerRef]);

  // Set up ResizeObserver to track content changes
  useEffect(() => {
    const container = contentContainerRef.current;
    if (!container) return;

    // Clean up existing observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    // Create new observer with debouncing
    let debounceTimeout: NodeJS.Timeout;
    resizeObserverRef.current = new ResizeObserver(() => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        measureSections();
      }, 100);
    });

    // Observe the container
    resizeObserverRef.current.observe(container);

    // Initial measurement
    measureSections();

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (measurementTimeoutRef.current) {
        clearTimeout(measurementTimeoutRef.current);
      }
      clearTimeout(debounceTimeout);
    };
  }, [contentContainerRef, measureSections]);

  // Re-measure when selection or order changes
  useEffect(() => {
    measureSections();
  }, [sectionOrder, selectedSections, measureSections]);

  // Calculate page layout based on measurements
  const layout = useMemo((): CalculatedLayout => {
    const pages: Page[] = [];
    const sectionToPage = new Map<string, number>();

    if (sectionHeights.size === 0) {
      // No measurements yet, return single page
      return {
        pages: [{
          pageNumber: 1,
          items: [],
          contentHeight: 0
        }],
        totalPages: 1,
        sectionToPage
      };
    }

    // Helper to create a new page
    const createNewPage = (): Page => ({
      pageNumber: pages.length + 1,
      items: [],
      contentHeight: 0
    });

    let currentPage: Page = createNewPage();

    // First page has less space due to header
    let availableHeight = pageContentHeight - headerHeight;
    let currentY = 0;

    // Process sections in order
    for (const sectionId of sectionOrder) {
      // Skip if not selected
      if (!selectedSections[sectionId]) continue;

      const sectionHeight = sectionHeights.get(sectionId) || 0;
      if (sectionHeight === 0) continue;

      // Check for manual page break after previous section
      const hasManualBreakBefore = currentPage.items.length > 0 &&
        manualPageBreaks.has(currentPage.items[currentPage.items.length - 1]?.sectionId);

      // Start a new page if manual break
      if (hasManualBreakBefore) {
        pages.push(currentPage);
        currentPage = createNewPage();
        currentY = 0;
        availableHeight = pageContentHeight;
      }

      // Calculate remaining space on current page
      const remainingSpace = availableHeight - currentY;

      // Case 1: Section fits entirely on current page
      if (sectionHeight <= remainingSpace) {
        currentPage.items.push({
          sectionId,
          height: sectionHeight,
          offsetY: currentY,
          isContinuation: false,
          hasMore: false
        });
        sectionToPage.set(sectionId, currentPage.pageNumber);
        currentY += sectionHeight;
        currentPage.contentHeight = currentY;
      }
      // Case 2: Section doesn't fit but current page is empty - section is oversized
      else if (currentPage.items.length === 0 && sectionHeight > availableHeight) {
        // Split the section across multiple pages
        let remainingSectionHeight = sectionHeight;
        let clipStart = 0;
        let isFirst = true;

        while (remainingSectionHeight > 0) {
          const spaceOnThisPage = isFirst ? availableHeight : pageContentHeight;
          const portionHeight = Math.min(remainingSectionHeight, spaceOnThisPage);
          const hasMore = remainingSectionHeight > portionHeight;

          currentPage.items.push({
            sectionId,
            height: portionHeight,
            offsetY: 0, // Always starts at top of page for split sections
            clipStartY: clipStart,
            clipEndY: clipStart + portionHeight,
            isContinuation: !isFirst,
            hasMore: hasMore,
            totalSectionHeight: sectionHeight
          });

          if (isFirst) {
            sectionToPage.set(sectionId, currentPage.pageNumber);
          }

          currentPage.contentHeight = portionHeight;
          clipStart += portionHeight;
          remainingSectionHeight -= portionHeight;

          // If more content remains, start a new page
          if (remainingSectionHeight > 0) {
            pages.push(currentPage);
            currentPage = createNewPage();
            availableHeight = pageContentHeight;
          }

          isFirst = false;
        }
        currentY = currentPage.contentHeight;
      }
      // Case 3: Section doesn't fit - split it to fill remaining space first
      // (Removed old behavior that moved entire sections to new pages, leaving blank space)
      else {
        // Split the section: first fill remaining space on current page, then continue on new pages
        let remainingSectionHeight = sectionHeight;
        let clipStart = 0;
        let isFirst = true;

        while (remainingSectionHeight > 0) {
          // Use remaining space on current page for first portion, full page height for subsequent
          const spaceOnThisPage = isFirst ? (availableHeight - currentY) : pageContentHeight;
          const portionHeight = Math.min(remainingSectionHeight, spaceOnThisPage);
          const hasMore = remainingSectionHeight > portionHeight;

          currentPage.items.push({
            sectionId,
            height: portionHeight,
            offsetY: isFirst ? currentY : 0, // Use currentY for first portion on existing page
            clipStartY: clipStart,
            clipEndY: clipStart + portionHeight,
            isContinuation: !isFirst,
            hasMore: hasMore,
            totalSectionHeight: sectionHeight
          });

          if (isFirst) {
            sectionToPage.set(sectionId, currentPage.pageNumber);
            currentY += portionHeight;
            currentPage.contentHeight = currentY;
          } else {
            currentPage.contentHeight = portionHeight;
          }

          clipStart += portionHeight;
          remainingSectionHeight -= portionHeight;

          if (remainingSectionHeight > 0) {
            pages.push(currentPage);
            currentPage = createNewPage();
            availableHeight = pageContentHeight;
            currentY = 0;
          }

          isFirst = false;
        }

        if (!isFirst) {
          currentY = currentPage.contentHeight;
        }
      }
    }

    // Add the last page if it has content
    if (currentPage.items.length > 0 || pages.length === 0) {
      pages.push(currentPage);
    }

    return {
      pages,
      totalPages: pages.length,
      sectionToPage
    };
  }, [
    sectionHeights,
    sectionOrder,
    selectedSections,
    manualPageBreaks,
    pageContentHeight,
    headerHeight
  ]);

  return {
    pages: layout.pages,
    totalPages: layout.totalPages,
    sectionToPage: layout.sectionToPage,
    measureSections,
    isCalculating,
    sectionHeights
  };
}

export default usePdfPageLayout;
